import React, { createContext, useState, useContext, useEffect,useCallback } from 'react';

/**
 * @typedef {Object} Product
 * @property {string} id
 * @property {string} name
 * @property {number} price
 * @property {string} [category]
 * @property {string} [imageUrl]
 */

const CartContext = createContext({
  cart: [],
  checkoutItems: [],
  addToCart: (product) => {},
  removeFromCart: (productId) => {},
  updateQuantity: (productId, quantity) => {},
  clearCart: () => {},
  buyNow: (product) => {},
  resetCheckout: () => {}
});

export const CartProvider = ({ children }) => {
  // Initialize cart with complete safeguards
  const [cart, setCart] = useState(() => {
    try {
      if (typeof window !== 'undefined') {
        const savedCart = localStorage.getItem('cart');
        const parsedCart = savedCart ? JSON.parse(savedCart) : [];
        return Array.isArray(parsedCart) ? parsedCart : [];
      }
      return [];
    } catch (error) {
      console.error('Failed to initialize cart:', error);
      return [];
    }
  });

  // Initialize checkoutItems as array
  const [checkoutItems, setCheckoutItems] = useState([]);

  // Enhanced cart persistence
  useEffect(() => {
    try {
      if (Array.isArray(cart)) {
        localStorage.setItem('cart', JSON.stringify(cart));
      } else {
        console.warn('Attempted to save non-array cart:', cart);
        localStorage.setItem('cart', JSON.stringify([]));
      }
    } catch (error) {
      console.error('Failed to persist cart:', error);
    }
  }, [cart]);

  // Enhanced addToCart with complete type safety
  const addToCart = (product) => {
    setCart(prevCart => {
      // Ensure we're always working with an array
      const currentCart = Array.isArray(prevCart) ? prevCart : [];
      
      const safeProduct = {
        id: String(product?.id || Math.random().toString(36).substr(2, 9)),
        name: String(product?.name || 'Unnamed Product'),
        price: Math.max(0, Number(product?.price) || 0),
        quantity: Math.max(1, Number(product?.quantity) || 1),
        category: String(product?.category || ''),
        imageUrl: String(product?.imageUrl || '')
      };

      const existingItemIndex = currentCart.findIndex(item => item.id === safeProduct.id);
      
      if (existingItemIndex >= 0) {
        return currentCart.map((item, index) => 
          index === existingItemIndex
            ? { 
                ...item, 
                quantity: item.quantity + safeProduct.quantity,
                price: safeProduct.price // Update price if changed
              }
            : item
        );
      }
      
      return [...currentCart, safeProduct];
    });
  };

  // Safe removeFromCart
  const removeFromCart = (productId) => {
    setCart(prevCart => {
      const currentCart = Array.isArray(prevCart) ? prevCart : [];
      return currentCart.filter(item => String(item.id) !== String(productId));
    });
  };

  // Enhanced quantity update
  const updateQuantity = (productId, quantity) => {
    const newQuantity = Math.max(0, Math.floor(Number(quantity)));
    
    setCart(prevCart => {
      const currentCart = Array.isArray(prevCart) ? prevCart : [];
      
      if (newQuantity <= 0) {
        return currentCart.filter(item => String(item.id) !== String(productId));
      }
      
      return currentCart.map(item =>
        String(item.id) === String(productId)
          ? { ...item, quantity: newQuantity }
          : item
      );
    });
  };

  // Clear cart remains same
  const clearCart = () => {
    setCart([]);
  };

  // Enhanced buyNow with complete safeguards
  const buyNow = (product) => {
    const safeProduct = {
      id: String(product?.id || Math.random().toString(36).substr(2, 9)),
      name: String(product?.name || 'Unnamed Product'),
      price: Math.max(0, Number(product?.price) || 0),
      quantity: 1, // Always 1 for Buy Now
      category: String(product?.category || ''),
      imageUrl: String(product?.imageUrl || ''),
      _timestamp: Date.now()
    };
    setCheckoutItems([safeProduct]);
  };

  // Reset checkout remains same
  const resetCheckout = useCallback(() => {
    setCheckoutItems([]);
  }, []);

  return (
    <CartContext.Provider 
      value={{ 
        cart: Array.isArray(cart) ? cart : [], // Final safeguard
        checkoutItems: Array.isArray(checkoutItems) ? checkoutItems : [],
        addToCart, 
        removeFromCart, 
        updateQuantity,
        clearCart,
        buyNow,
        resetCheckout
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};